import axios from 'axios';
import { env } from 'env';

import type { IMapLayout } from '@interfaces/map';
import type { IBrandingEntityValues } from '@interfaces/rooms';
import type { IMapSpotLocationPreview } from '@interfaces/map';

export type { IBrandingEntityValues };

export interface IColorScheme {
  primaryColor?: string;
  secondaryColor?: string;
  outlineColor?: string;
}

export interface IBrandingApi {
  brandingId: string;
  mapId?: string;
  status?: string;
  name?: string;
  timestamp?: number;
  brandingColors?: IColorScheme;
  layout?: IMapLayout;
  brandingEntities?: IBrandingEntityValues;
  [key: string]: unknown;
}

export interface IUpdateBrandingData {
  name?: string;
  status?: string;
  brandingColors?: IColorScheme;
  layout?: IMapLayout;
  // Per-entity replace: every entity named here is stored with exactly the
  // properties listed, and entities left out keep the values they had.
  brandingEntities?: IBrandingEntityValues;
}

export interface IBrandingsResponse {
  brandings: IBrandingApi[];
  lastKey?: string;
}

export interface ICreateBrandingData {
  mapId?: string;
  status?: string;
  parentBrandingId?: string;
  author?: string;
  [key: string]: unknown;
}

export interface ISpotLocalization {
  location?: string;
  url?: string;
  previewImage?: string;
  id?: string;
  md5?: string;
  filename?: string;
  previewFilename?: string;
  [key: string]: unknown;
}

export interface ISpotLocalizationsMap {
  [languageCode: string]: ISpotLocalization;
}

export interface IBrandingSpotApi extends IMapSpotLocationPreview {
  brandingSpotId?: string;
  brandingId?: string;
  type?: string;
  name?: string;
  ueIdentifier?: string;
  roomSpotId?: string;
  roomId?: string;
  timestamp?: number;
  data?: {
    location?: string;
    url?: string;
    id?: string;
    [key: string]: unknown;
  };
  isActive?: boolean;
  previewImage?: string;
  md5?: string;
  distance?: number | null;
  volume?: number;
  isMuted?: boolean;
  localizations?: ISpotLocalizationsMap;
  [key: string]: unknown;
}

export interface ICreateBrandingSpotData {
  isActive: boolean;
  type?: string;
  name?: string;
  ueIdentifier?: string;
  roomSpotId?: string;
  roomId?: string;
  previewImage?: string;
  md5?: string;
  distance?: number | null;
  volume?: number;
  isMuted?: boolean;
  data: { location: string; url: string; id?: string; [key: string]: unknown };
  localizations?: ISpotLocalizationsMap;
}

export interface IUpdateBrandingSpotData {
  isActive?: boolean;
  type?: string;
  name?: string;
  ueIdentifier?: string;
  roomSpotId?: string;
  roomId?: string;
  previewImage?: string;
  md5?: string;
  distance?: number | null;
  volume?: number;
  isMuted?: boolean;
  data?: {
    location?: string;
    url?: string;
    id?: string;
    [key: string]: unknown;
  };
  localizations?: ISpotLocalizationsMap;
}

export interface IBrandingHotspotData {
  location?: string;
  url?: string;
  webBrowserUrl?: string;
  id?: string;
  md5?: string;
  [key: string]: unknown;
}

export interface IBrandingHotspotApi {
  brandingHotspotId?: string;
  brandingId?: string;
  mapId?: string;
  type?: string;
  name?: string;
  timestamp?: number;
  isActive?: boolean;
  // Absent or `true` — the object reacts to clicks and hover in world.
  // `false` — fully static scenery (UE binds no click / highlight events).
  isInteractive?: boolean;
  position?: number[];
  rotation?: number[];
  scale?: number | number[];
  data?: IBrandingHotspotData;
  localizations?: ISpotLocalizationsMap;
  [key: string]: unknown;
}

export interface ICreateBrandingHotspotData {
  brandingHotspotId?: string;
  isActive: boolean;
  isInteractive?: boolean;
  type?: string;
  name?: string;
  mapId?: string;
  position?: number[];
  rotation?: number[];
  scale?: number | number[];
  data: IBrandingHotspotData & { url: string };
  localizations?: ISpotLocalizationsMap;
}

export interface IUpdateBrandingHotspotData {
  isActive?: boolean;
  isInteractive?: boolean;
  type?: string;
  name?: string;
  mapId?: string;
  position?: number[];
  rotation?: number[];
  scale?: number | number[];
  md5?: string;
  data?: IBrandingHotspotData;
  localizations?: ISpotLocalizationsMap;
}

export default class BrandingsService {
  static getBrandings(params: {
    mapId?: string;
    limit?: number;
    startKey?: string;
    status?: string;
  }): Promise<IBrandingsResponse> {
    const searchParams = new URLSearchParams();
    if (params.mapId) searchParams.set('mapId', params.mapId);
    if (params.limit != null) searchParams.set('limit', String(params.limit));
    if (params.startKey) searchParams.set('startKey', params.startKey);
    if (params.status) searchParams.set('status', params.status);

    const query = searchParams.toString();
    const url = `${env.REACT_APP_API_URL}api/v2/brandings${
      query ? `?${query}` : ''
    }`;

    return axios.get<IBrandingsResponse>(url).then((res) => res.data);
  }

  static getBranding(brandingId: string): Promise<IBrandingApi> {
    return axios
      .get<IBrandingApi>(
        `${env.REACT_APP_API_URL}api/v2/brandings/${brandingId}`,
      )
      .then((res) => res.data);
  }

  static createBranding(data: ICreateBrandingData): Promise<IBrandingApi> {
    return axios
      .post<IBrandingApi>(`${env.REACT_APP_API_URL}api/v2/brandings`, data)
      .then((res) => res.data);
  }

  static updateBranding(
    brandingId: string,
    data: IUpdateBrandingData,
  ): Promise<IBrandingApi> {
    return axios
      .patch<IBrandingApi>(
        `${env.REACT_APP_API_URL}api/v2/brandings/${brandingId}`,
        data,
      )
      .then((res) => res.data);
  }

  static activateBranding(
    brandingId: string,
    roomId: string,
  ): Promise<IBrandingApi> {
    return axios
      .post<IBrandingApi>(
        `${env.REACT_APP_API_URL}api/v2/brandings/${brandingId}/activate`,
        { roomId },
      )
      .then((res) => res.data);
  }

  static deleteBranding(brandingId: string): Promise<void> {
    return axios
      .delete<void>(`${env.REACT_APP_API_URL}api/v2/brandings/${brandingId}`)
      .then((res) => res.data);
  }

  static getBrandingSpots(
    brandingId: string,
    params?: { limit?: number; startKey?: string; isActive?: boolean },
  ): Promise<{ spots: IBrandingSpotApi[]; lastKey?: unknown }> {
    const searchParams = new URLSearchParams();
    if (params?.limit != null) searchParams.set('limit', String(params.limit));
    if (params?.startKey) searchParams.set('startKey', params.startKey);
    if (params?.isActive != null)
      searchParams.set('isActive', String(params.isActive));
    const query = searchParams.toString();
    const url = `${env.REACT_APP_API_URL}api/v2/brandings/${brandingId}/spots${
      query ? `?${query}` : ''
    }`;
    return axios.get(url).then((res) => res.data);
  }

  static createBrandingSpot(
    brandingId: string,
    data: ICreateBrandingSpotData,
  ): Promise<IBrandingSpotApi> {
    return axios
      .post<IBrandingSpotApi>(
        `${env.REACT_APP_API_URL}api/v2/brandings/${brandingId}/spots`,
        data,
      )
      .then((res) => res.data);
  }

  static patchBrandingSpot(
    brandingId: string,
    brandingSpotId: string,
    data: IUpdateBrandingSpotData,
  ): Promise<IBrandingSpotApi> {
    return axios
      .patch<IBrandingSpotApi>(
        `${env.REACT_APP_API_URL}api/v2/brandings/${brandingId}/spots/${brandingSpotId}`,
        data,
      )
      .then((res) => res.data);
  }

  static deleteBrandingSpot(
    brandingId: string,
    brandingSpotId: string,
  ): Promise<void> {
    return axios
      .delete<void>(
        `${env.REACT_APP_API_URL}api/v2/brandings/${brandingId}/spots/${brandingSpotId}`,
      )
      .then((res) => res.data);
  }

  static getBrandingHotspots(
    brandingId: string,
    params?: { limit?: number; startKey?: string; isActive?: boolean },
  ): Promise<{ hotspots: IBrandingHotspotApi[]; lastKey?: unknown }> {
    const searchParams = new URLSearchParams();
    if (params?.limit != null) {
      searchParams.set('limit', String(params.limit));
    }
    if (params?.startKey) {
      searchParams.set('startKey', params.startKey);
    }
    if (params?.isActive != null) {
      searchParams.set('isActive', String(params.isActive));
    }
    const query = searchParams.toString();
    const url = `${
      env.REACT_APP_API_URL
    }api/v2/brandings/${brandingId}/hotspots${query ? `?${query}` : ''}`;
    return axios.get(url).then((res) => res.data);
  }

  static createBrandingHotspot(
    brandingId: string,
    data: ICreateBrandingHotspotData,
  ): Promise<IBrandingHotspotApi> {
    return axios
      .post<IBrandingHotspotApi>(
        `${env.REACT_APP_API_URL}api/v2/brandings/${brandingId}/hotspots`,
        data,
      )
      .then((res) => res.data);
  }

  static patchBrandingHotspot(
    brandingId: string,
    brandingHotspotId: string,
    data: IUpdateBrandingHotspotData,
  ): Promise<IBrandingHotspotApi> {
    return axios
      .patch<IBrandingHotspotApi>(
        `${env.REACT_APP_API_URL}api/v2/brandings/${brandingId}/hotspots/${brandingHotspotId}`,
        data,
      )
      .then((res) => res.data);
  }

  static deleteBrandingHotspot(
    brandingId: string,
    brandingHotspotId: string,
  ): Promise<void> {
    return axios
      .delete<void>(
        `${env.REACT_APP_API_URL}api/v2/brandings/${brandingId}/hotspots/${brandingHotspotId}`,
      )
      .then((res) => res.data);
  }
}
